import axios from 'lib/axiosConfigWithoutHumps'
import { contentLanguages } from 'lib/languages'
import { formatMultiLanguageField } from 'lib/utils'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { MasterProduct } from 'types/api/product'
import { useNutrientHeader } from './useNutrientHeader'
import { useProductAttribute } from './useProductAttribute'

// some fields from pim-v2 masterproducts are in snake_case, due to the PUT nature of the masterproducts request, imported axios config does not convert fields to camel case

export const useProduct = (masterProductId: string) => {
  const {
    isLoading,
    error,
    data: response,
  } = useQuery(['getMasterProductInfo', masterProductId], async () =>
    axios.get(`/v2/master-products/${masterProductId}?data=full`)
  )
  const product = (response?.data.data as MasterProduct) || []
  return { isLoading, error, product }
}

export const usePublishProduct = (masterProductId: string) => {
  const queryClient = useQueryClient()
  return useMutation(
    (values: Record<string, never>) =>
      axios.post(`/v2/master-products/${masterProductId}/public`, values),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getMasterProduct', masterProductId])
      },
      onSuccess: () => {
        queryClient.refetchQueries(['getMasterProductInfo', masterProductId])
      },
    }
  )
}

export const useGetMasterProductFields = (
  masterProductId: string,
  product: MasterProduct,
  isLoadingMasterProduct: boolean,
  masterProductError: unknown
) => {
  if (isLoadingMasterProduct || masterProductError)
    return { product: {}, isLoadingMasterProduct, masterProductError }

  // product type field
  const { extensions } = product.tradeItem.tradeItemInformation

  const productType = () => {
    if (extensions.dgExtraProductModule?.extraProduct) {
      return extensions.dgExtraProductModule.extraProduct
    }
    if (extensions.foodAndBeverageIngredientModule?.x_isFoodOrBeverage) {
      return 'FOOD_OR_BEVERAGE'
    }

    // No known product type.
    return ''
  }

  const getFormattedListField = (
    arr: { $: string; '@languageCode': string }[]
  ) => {
    if (!arr || arr.length === 0) {
      return contentLanguages.reduce(
        (acc: { [lang: string]: string }, lang: string) => {
          acc[lang] = ''
          return acc
        },
        {}
      )
    }
    const formattedData = arr.reduce(
      (
        acc: { [x: string]: string[] },
        name: { $: string; '@languageCode': string }
      ) => {
        if (!acc[name['@languageCode']]) {
          acc[name['@languageCode']] = []
        }
        acc[name['@languageCode']].push(name.$)
        return acc
      },
      {}
    )

    return contentLanguages.reduce(
      (acc: { [lang: string]: string }, lang: string) => {
        if (lang in formattedData) {
          acc[lang] = formattedData[lang].join(', ')
        } else {
          acc[lang] = ''
        }
        return acc
      },
      {}
    )
  }

  const productName = formatMultiLanguageField(
    extensions.tradeItemDescriptionModule?.tradeItemDescriptionInformation
      ?.descriptionShort
  )

  const description = formatMultiLanguageField(
    extensions.tradeItemDescriptionModule?.tradeItemDescriptionInformation
      ?.tradeItemDescription
  )

  const brand = formatMultiLanguageField(
    extensions.tradeItemDescriptionModule?.tradeItemDescriptionInformation
      ?.brandNameInformation?.languageSpecificBrandName
  )
  const untranslatedBrand =
    extensions.tradeItemDescriptionModule?.tradeItemDescriptionInformation
      ?.brandNameInformation?.brandName

  const countryCode =
    extensions.placeOfItemActivityModule?.placeOfProductActivity
      ?.countryOfOrigin?.length > 0
      ? extensions.placeOfItemActivityModule?.placeOfProductActivity
          ?.countryOfOrigin[0].countryCode
      : ''

  const countryOfOrigin = formatMultiLanguageField(
    extensions.placeOfItemActivityModule?.placeOfProductActivity
      ?.countryOfOriginStatement
  )

  const categories =
    extensions.dgPresentationModule?.presentationCategory &&
    extensions.dgPresentationModule?.presentationCategory.length > 0
      ? extensions.dgPresentationModule?.presentationCategory.map(
          (category) => {
            return {
              tree: category.treeName,
              category: { extId: category.extId },
            }
          }
        )
      : null

  const functionalName = formatMultiLanguageField(
    extensions.tradeItemDescriptionModule.tradeItemDescriptionInformation
      .functionalName
  )
  const variantName = formatMultiLanguageField(
    extensions.tradeItemDescriptionModule.tradeItemDescriptionInformation
      .variantDescription
  )
  const searchKeywords = getFormattedListField(
    extensions.marketingInformationModule?.marketingInformation
      ?.tradeItemKeyWords
  )
  const dietTypes = extensions.dietInformationModule?.dietInformation
    ?.dietTypeInformation
    ? extensions.dietInformationModule?.dietInformation?.dietTypeInformation
    : []

  const netContent =
    extensions.tradeItemMeasurementsModule?.tradeItemMeasurements?.netContent
      .length > 0
      ? extensions.tradeItemMeasurementsModule?.tradeItemMeasurements
          ?.netContent[0].$
      : null

  const netContentUnit =
    extensions.tradeItemMeasurementsModule?.tradeItemMeasurements?.netContent
      .length > 0
      ? extensions.tradeItemMeasurementsModule?.tradeItemMeasurements
          ?.netContent[0]['@measurementUnitCode']
      : null

  const pricedBy =
    extensions.salesInformationModule?.salesInformation
      ?.priceByMeasureTypeCode || ''

  const variableTradeItemTypeCode =
    extensions.variableTradeItemInformationModule?.variableTradeItemInformation
      ?.variableTradeItemTypeCode || ''

  const salesConditions =
    extensions.salesInformationModule?.salesInformation
      ?.consumerSalesConditionCode || []

  const packagingType =
    extensions.packagingInformationModule?.packaging?.[0]?.packagingTypeCode ||
    ''

  const packagingMaterial =
    extensions.packagingInformationModule?.packaging?.[0]?.packagingMaterial ||
    []

  const width =
    extensions.tradeItemMeasurementsModule?.tradeItemMeasurements?.width?.$ ||
    null
  const widthUnit =
    extensions.tradeItemMeasurementsModule?.tradeItemMeasurements?.width?.[
      '@measurementUnitCode'
    ] || null

  const height =
    extensions.tradeItemMeasurementsModule?.tradeItemMeasurements?.height?.$ ||
    null
  const heightUnit =
    extensions.tradeItemMeasurementsModule?.tradeItemMeasurements?.height?.[
      '@measurementUnitCode'
    ] || null

  const depth =
    extensions.tradeItemMeasurementsModule?.tradeItemMeasurements?.depth?.$ ||
    null
  const depthUnit =
    extensions.tradeItemMeasurementsModule?.tradeItemMeasurements?.depth?.[
      '@measurementUnitCode'
    ] || null

  const grossWeight =
    extensions.tradeItemMeasurementsModule?.tradeItemMeasurements
      ?.tradeItemWeight?.grossWeight?.$ || null
  const grossWeightUnit =
    extensions.tradeItemMeasurementsModule?.tradeItemMeasurements
      ?.tradeItemWeight?.grossWeight?.['@measurementUnitCode'] || null
  const nutrientHeaders = useNutrientHeader(
    extensions.nutritionalInformationModule?.nutrientHeader
  )

  const preparationUsage = formatMultiLanguageField(
    extensions.foodAndBeveragePreparationServingModule?.preparationServing?.[0]
      ?.preparationInstructions
  )
  const storageInformation = formatMultiLanguageField(
    extensions.consumerInstructionsModule?.consumerInstructions
      ?.consumerStorageInstructions
  )

  const productAttributeGroup = useProductAttribute(
    extensions.dgProductAttributeModule?.productAttributeGroup || []
  )

  const equivalentProducts =
    product.tradeItem.referencedTradeItem?.filter(
      (tradeItem) => tradeItem.referencedTradeItemTypeCode === 'EQUIVALENT'
    ) || []

  const alternativeProducts =
    product.tradeItem.referencedTradeItem?.filter(
      (tradeItem) =>
        tradeItem.referencedTradeItemTypeCode === 'PRIMARY_ALTERNATIVE'
    ) || []

  return {
    product: {
      gtin: product.gtin,
      productName,
      description,
      countryCode,
      countryOfOrigin,
      categories,
      brand,
      productType: productType() as string,
      visibleToConsumers:
        extensions.dgPresentationModule?.productAbsoluteConsumerVisibility ||
        false,
      percentageOfAlcohol:
        extensions.alcoholInformationModule?.alcoholInformation
          ?.percentageOfAlcoholByVolume || null,
      dietTypes,
      functionalName,
      variantName,
      searchKeywords,
      boostValue: extensions.dgPresentationModule?.searchWeightFactor0 || 0.5,
      displayUnit:
        extensions.salesInformationModule?.salesInformation
          ?.x_sellingUnitOfMeasureCode || '',
      netContent,
      netContentUnit,
      pricedBy,
      pickedBy:
        extensions.salesInformationModule?.salesInformation
          ?.x_sellingByMeasureTypeCode || '',
      variableTradeItemTypeCode,
      initialValue:
        extensions.salesInformationModule?.salesInformation
          ?.x_sellingContentInitial || null,
      incrementValue:
        extensions.salesInformationModule?.salesInformation
          ?.x_sellingContentIncrement || null,
      salesConditions,
      untranslatedBrand,
      packagingType,
      packagingMaterial,
      width,
      widthUnit,
      height,
      heightUnit,
      depth,
      depthUnit,
      grossWeight,
      grossWeightUnit,
      nutrientHeaders,
      preparationUsage,
      storageInformation,
      productAttributeGroup,
      equivalentProducts,
      alternativeProducts,
    },
  }
}

export const usePutMasterProduct = (masterProductId: string) => {
  const queryClient = useQueryClient()
  return useMutation(
    (values: Partial<MasterProduct>) =>
      axios.put(`/v2/master-products/${masterProductId}`, values),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getMasterProductInfo', masterProductId])
      },
      onSuccess: () => {
        queryClient.fetchQuery(['getMasterProductInfo', masterProductId])
      },
    }
  )
}
